import React, { createContext, useCallback, useContext, useRef } from "react";

/**
 * Lets the data picker insert `{{3.text}}` into whichever mapping field the
 * user last focused, at the caret.
 */
interface MappingTarget {
    insert: (text: string) => void;
}

interface MappingContextValue {
    setTarget: (target: MappingTarget | null) => void;
    insert: (text: string) => boolean;
}

export const MappingContext = createContext<MappingContextValue>({ setTarget: () => undefined, insert: () => false });

export const MappingProvider = ({ children }: { children: React.ReactNode }) => {
    const target = useRef<MappingTarget | null>(null);
    const setTarget = useCallback((t: MappingTarget | null) => {
        target.current = t;
    }, []);
    const insert = useCallback((text: string) => {
        if (!target.current) {
            return false;
        }
        target.current.insert(text);

        return true;
    }, []);

    return <MappingContext.Provider value={{ setTarget, insert }}>{children}</MappingContext.Provider>;
};

interface MappingInputProps {
    value: any;
    onChange: (value: string) => void;
    multiline?: boolean;
    placeholder?: string;
    invalid?: boolean;
}

/** A text field that accepts {{expressions}}; the data picker types into it. */
const MappingInput = ({ value, onChange, multiline, placeholder, invalid }: MappingInputProps) => {
    const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
    const { setTarget } = useContext(MappingContext);
    const text = value === null || value === undefined ? '' : typeof value === 'string' ? value : JSON.stringify(value);

    const onFocus = () => setTarget({
        insert: (snippet: string) => {
            const el = ref.current;
            const start = el ? el.selectionStart ?? text.length : text.length;
            const end = el ? el.selectionEnd ?? text.length : text.length;
            const next = text.slice(0, start) + snippet + text.slice(end);
            onChange(next);
            requestAnimationFrame(() => {
                if (el) {
                    el.focus();
                    el.selectionStart = el.selectionEnd = start + snippet.length;
                }
            });
        },
    });

    const props = {
        ref,
        className: "form-control" + (invalid ? " is-invalid" : ""),
        value: text,
        placeholder,
        onFocus,
        onChange: (e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) => onChange(e.target.value),
    };

    return multiline || text.length > 60 || text.includes('\n')
        ? <textarea {...props} rows={Math.min(8, Math.max(2, text.split('\n').length))} />
        : <input type="text" {...props} />;
};

export default MappingInput;
